import React, { useCallback, useMemo } from 'react'
import { GetControlEvidenceTabRequest } from '@trustero/trustero-api-web/lib/attachment/attachment_pb'
import { AttachmentPromiseClient } from '@trustero/trustero-api-web/lib/attachment/attachment_grpc_web_pb'
import { ModalFormId } from 'src/components/ModalForms/ModalForm'
import { useGrpcRevalidateByMethod } from 'src/components/async'
import { useIsShowModal } from 'src/Modal/ModalStateContext'
import {
  useControlEvidenceTabRequest,
  useLinkEvidenceToControl,
} from 'src/pages/Evidence/evidence.hooks'
import { LinkExistingEvidenceModal } from './LinkExistingEvidenceModal'

/**
 * Links existing account evidence to a control.
 */
export const LinkEvidenceToControlModal = ({
  controlId,
  controlName,
}: {
  controlId: string
  controlName?: string
}): JSX.Element => {
  const show = useIsShowModal(ModalFormId.LINK_EVIDENCE_TO_CONTROL)
  const revalidate = useGrpcRevalidateByMethod()
  const linkEvidenceToControl = useLinkEvidenceToControl()

  // Evidence already linked to this control, to exclude from the list.
  const controlEvidenceReq = useMemo(
    () => new GetControlEvidenceTabRequest().setControlModelId(controlId),
    [controlId],
  )
  const { data: linkedData } = useControlEvidenceTabRequest(
    controlEvidenceReq,
    show,
  )
  const excludedEvidenceIds = useMemo(
    () =>
      new Set(
        (linkedData?.getItemsList() ?? []).map((wrapper) =>
          wrapper.getEvidenceId(),
        ),
      ),
    [linkedData],
  )

  const onLink = useCallback(
    async (evidenceIds: string[]) => {
      await linkEvidenceToControl(controlId, evidenceIds)
      await revalidate(
        AttachmentPromiseClient.prototype.getControlEvidenceTabView,
      )
    },
    [linkEvidenceToControl, controlId, revalidate],
  )

  return (
    <LinkExistingEvidenceModal
      modalId={ModalFormId.LINK_EVIDENCE_TO_CONTROL}
      title="Link Evidence"
      description={
        controlName
          ? `The selected evidence will be linked to ${controlName}.`
          : 'The selected evidence will be linked to this control.'
      }
      excludedEvidenceIds={excludedEvidenceIds}
      onLink={onLink}
    />
  )
}
